import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';
import SearchableSelect from '../components/SearchableSelect';
import DateTimeFields from '../components/DateTimeFields';
import ScopeChecklist from '../components/ScopeChecklist';
import { cardClass, inputClass } from '../components/PageHeader';
import { toLocalDatetime } from '../utils/datetime';
import {
  JOB_STATUS_LABELS,
  PHOTO_PHASE_LABELS,
  jobStatusOptions,
} from '../utils/statusConstants';
import {
  PROPERTY_CATEGORY_LABELS,
  SERVICE_TYPE_LABELS,
  getScopeItems,
  getServiceTypesForCategory,
  propertyCategoryOptions,
  serviceTypeOptionsForCategory,
} from '../utils/cleaningCatalog';

const JobDetails = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [editForm, setEditForm] = useState(null);

  const load = useCallback(async () => {
    const res = await apiFetch(`/admin/jobs/${id}`);
    setData(res.data);
    const j = res.data.job;
    setNewStatus(j.status);
    const property_category =
      j.property_category ||
      (j.account_type === 'business' ? 'commercial' : 'residential');
    setEditForm({
      account_id: j.account_id,
      service_location_id: j.service_location_id,
      property_category,
      service_type: j.service_type,
      scope_items: Array.isArray(j.scope_items) ? j.scope_items : [],
      scheduled_start: toLocalDatetime(j.scheduled_start),
      scheduled_end: toLocalDatetime(j.scheduled_end),
      notes: j.notes || '',
      internal_notes: j.internal_notes || '',
    });
  }, [id]);

  useEffect(() => {
    load();
    apiFetch('/admin/vendors').then((r) => setVendors(r.data || []));
    apiFetch('/admin/accounts').then((r) => setAccounts(r.data || []));
  }, [load]);

  useEffect(() => {
    if (!editForm?.account_id) {
      setLocations([]);
      return;
    }
    apiFetch(`/admin/locations?account_id=${editForm.account_id}`).then((r) =>
      setLocations(r.data || [])
    );
  }, [editForm?.account_id]);

  const accountOptions = useMemo(
    () => accounts.map((a) => ({ value: a.id, label: a.name })),
    [accounts]
  );

  const locationOptions = useMemo(
    () =>
      locations.map((l) => ({
        value: l.id,
        label: `${l.label || l.address_line1} — ${l.city}`,
      })),
    [locations]
  );

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.id, label: v.display_name })),
    [vendors]
  );

  const serviceTypeOptions = useMemo(
    () =>
      editForm?.property_category
        ? serviceTypeOptionsForCategory(editForm.property_category)
        : [],
    [editForm?.property_category]
  );

  const scopeItems = useMemo(
    () =>
      editForm?.property_category && editForm?.service_type
        ? getScopeItems(editForm.property_category, editForm.service_type)
        : [],
    [editForm?.property_category, editForm?.service_type]
  );

  if (!data || !editForm) return <p className="text-slate-500">Loading…</p>;

  const { job, photos, transitions } = data;

  const updateStatus = async () => {
    try {
      await apiFetch(`/admin/jobs/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success('Status updated');
      load();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    }
  };

  const assignVendor = async () => {
    if (!vendorId) return;
    try {
      await apiFetch(`/admin/jobs/${id}/assign-vendor`, {
        method: 'POST',
        body: JSON.stringify({ vendor_id: vendorId }),
      });
      toast.success('Vendor assigned');
      load();
    } catch (err) {
      toast.error(err.message || 'Assign failed');
    }
  };

  const saveJob = async (e) => {
    e.preventDefault();
    if (editForm.scope_items.length === 0) {
      toast.error('Select at least one item in the scope checklist');
      return;
    }
    try {
      await apiFetch(`/admin/jobs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          account_id: editForm.account_id,
          service_location_id: editForm.service_location_id,
          property_category: editForm.property_category,
          service_type: editForm.service_type,
          scope_items: editForm.scope_items,
          scheduled_start: editForm.scheduled_start || null,
          scheduled_end: editForm.scheduled_end || null,
          notes: editForm.notes,
          internal_notes: editForm.internal_notes,
        }),
      });
      toast.success('Job updated');
      load();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    }
  };

  return (
    <div>
      <Link to="/jobs" className="text-teal-700 text-sm mb-4 inline-block">
        ← Back to jobs
      </Link>
      <h1 className="text-2xl font-semibold mb-2">{job.job_number}</h1>
      <p className="text-slate-600 mb-6">
        {job.account_name} ·{' '}
        {PROPERTY_CATEGORY_LABELS[job.property_category] ||
          PROPERTY_CATEGORY_LABELS[editForm.property_category] ||
          '—'}{' '}
        · {SERVICE_TYPE_LABELS[job.service_type] || job.service_type} ·{' '}
        {JOB_STATUS_LABELS[job.status]}
      </p>

      <form onSubmit={saveJob} className={`${cardClass} p-6 mb-6 space-y-4`}>
        <h2 className="font-medium text-slate-900">Edit job</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <SearchableSelect
            label="Property type"
            value={editForm.property_category}
            onChange={(property_category) => {
              const types = getServiceTypesForCategory(property_category);
              setEditForm({
                ...editForm,
                property_category,
                service_type: types.includes(editForm.service_type)
                  ? editForm.service_type
                  : types[0] || '',
                scope_items: [],
              });
            }}
            options={propertyCategoryOptions}
            placeholder="Residential or commercial…"
            required
          />
          <SearchableSelect
            label="Cleaning type"
            value={editForm.service_type}
            onChange={(service_type) =>
              setEditForm({ ...editForm, service_type, scope_items: [] })
            }
            options={serviceTypeOptions}
            placeholder="Select cleaning type…"
            required
          />
          <SearchableSelect
            label="Account"
            value={editForm.account_id}
            onChange={(account_id) =>
              setEditForm({
                ...editForm,
                account_id,
                service_location_id: '',
              })
            }
            options={accountOptions}
            placeholder="Search accounts…"
            required
          />
          <SearchableSelect
            label="Location"
            value={editForm.service_location_id}
            onChange={(service_location_id) =>
              setEditForm({ ...editForm, service_location_id })
            }
            options={locationOptions}
            placeholder="Search locations…"
            required
          />
          <DateTimeFields
            label="Scheduled start"
            value={editForm.scheduled_start}
            onChange={(scheduled_start) => setEditForm({ ...editForm, scheduled_start })}
          />
          <DateTimeFields
            label="Scheduled end"
            value={editForm.scheduled_end}
            onChange={(scheduled_end) => setEditForm({ ...editForm, scheduled_end })}
          />
        </div>
        <ScopeChecklist
          items={scopeItems}
          selected={editForm.scope_items}
          onChange={(scope_items) => setEditForm({ ...editForm, scope_items })}
        />
        <div>
          <label className="block text-sm font-medium mb-1">Customer notes</label>
          <textarea
            className={inputClass}
            rows={2}
            value={editForm.notes}
            onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Internal notes</label>
          <textarea
            className={inputClass}
            rows={2}
            value={editForm.internal_notes}
            onChange={(e) => setEditForm({ ...editForm, internal_notes: e.target.value })}
          />
        </div>
        <button
          type="submit"
          className="bg-teal-700 text-white px-4 py-2 rounded-lg text-sm hover:bg-teal-800"
        >
          Save changes
        </button>
      </form>

      <div className="grid md:grid-cols-2 gap-6">
        <section className={`${cardClass} p-6`}>
          <h2 className="font-medium mb-3">Site</h2>
          <p className="text-sm text-slate-700">
            {job.address_line1}
            {job.address_line2 ? `, ${job.address_line2}` : ''}
            <br />
            {job.city}, {job.state} {job.postal_code}
          </p>
          {job.access_notes && (
            <p className="text-sm text-slate-500 mt-2">Access: {job.access_notes}</p>
          )}
          <p className="text-sm mt-2">
            Vendor: <strong>{job.vendor_name || 'Unassigned'}</strong>
          </p>
        </section>

        <section className={`${cardClass} p-6 space-y-4`}>
          <h2 className="font-medium">Dispatch</h2>
          <div>
            <label className="block text-sm mb-1">Status</label>
            <div className="flex gap-2">
              <SearchableSelect
                value={newStatus}
                onChange={setNewStatus}
                options={jobStatusOptions}
                placeholder="Search status…"
                className="flex-1"
              />
              <button
                type="button"
                onClick={updateStatus}
                className="bg-slate-800 text-white px-3 py-2 rounded-lg text-sm shrink-0"
              >
                Save
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm mb-1">Assign vendor</label>
            <div className="flex gap-2">
              <SearchableSelect
                value={vendorId}
                onChange={setVendorId}
                options={vendorOptions}
                placeholder="Search vendors…"
                allowEmpty
                emptyLabel="Select vendor"
                className="flex-1"
              />
              <button
                type="button"
                onClick={assignVendor}
                className="bg-teal-700 text-white px-3 py-2 rounded-lg text-sm shrink-0"
              >
                Assign
              </button>
            </div>
          </div>
        </section>
      </div>

      <section className={`${cardClass} p-6 mt-6`}>
        <h2 className="font-medium mb-3">Photos ({photos.length})</h2>
        {photos.length === 0 ? (
          <p className="text-sm text-slate-500">No photos yet.</p>
        ) : (
          <ul className="text-sm space-y-1">
            {photos.map((p) => (
              <li key={p.id}>
                {PHOTO_PHASE_LABELS[p.phase] || p.phase}: {p.storage_key}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={`${cardClass} p-6 mt-6`}>
        <h2 className="font-medium mb-3">Status history</h2>
        <ul className="text-sm space-y-2">
          {transitions.map((t) => (
            <li key={t.id} className="text-slate-600">
              {t.from_status ? JOB_STATUS_LABELS[t.from_status] || t.from_status : '—'} →{' '}
              {JOB_STATUS_LABELS[t.to_status] || t.to_status}{' '}
              <span className="text-slate-400">
                {new Date(t.created_at).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default JobDetails;
